// src/pages/finance-portal/stripe-payments/StripePayments.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, InputAdornment,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoIcon from '@mui/icons-material/Undo';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val, currency = 'usd') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format((val || 0) / 100);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(typeof dateStr === 'number' ? dateStr * 1000 : dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_COLORS = {
    succeeded: { bg: '#dcfce7', color: '#166534' },
    processing: { bg: '#dbeafe', color: '#1e40af' },
    requires_payment_method: { bg: '#fef3c7', color: '#92400e' },
    requires_confirmation: { bg: '#fef3c7', color: '#92400e' },
    requires_action: { bg: '#fef3c7', color: '#92400e' },
    canceled: { bg: '#fecaca', color: '#991b1b' },
    requires_capture: { bg: '#e0e7ff', color: '#3730a3' },
};

const PAYMENT_METHOD_OPTIONS = [
    { value: 'card', label: 'Card' },
    { value: 'us_bank_account', label: 'US Bank Account' },
    { value: 'ach_debit', label: 'ACH Debit' },
];

const CURRENCY_OPTIONS = ['usd', 'eur', 'gbp', 'cad'];

export default function StripePayments() {
    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ amount: '', currency: 'usd', payment_method_types: ['card'] });
    const [creating, setCreating] = useState(false);

    // Refund dialog
    const [refundDialog, setRefundDialog] = useState({ open: false, payment: null });
    const [refundAmount, setRefundAmount] = useState('');
    const [refunding, setRefunding] = useState(false);

    const fetchPayments = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/payment-intents');
            if (res.data.success) {
                setPayments(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payment intents');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);

    const handleCreate = async () => {
        setCreating(true);
        try {
            const payload = {
                amount: Math.round(parseFloat(createForm.amount) * 100),
                currency: createForm.currency,
                payment_method_types: createForm.payment_method_types,
            };
            await axiosInstance.post('/api/finance/stripe/payment-intents', payload);
            setSuccess('Payment intent created');
            setCreateOpen(false);
            setCreateForm({ amount: '', currency: 'usd', payment_method_types: ['card'] });
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create payment intent');
        } finally {
            setCreating(false);
        }
    };

    const handleRefund = async () => {
        setRefunding(true);
        try {
            const payload = {
                payment_intent_id: refundDialog.payment.id,
            };
            if (refundAmount) {
                payload.amount = Math.round(parseFloat(refundAmount) * 100);
            }
            await axiosInstance.post('/api/finance/stripe/refunds', payload);
            setSuccess('Refund initiated');
            setRefundDialog({ open: false, payment: null });
            setRefundAmount('');
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create refund');
        } finally {
            setRefunding(false);
        }
    };

    const togglePaymentMethod = (method) => {
        setCreateForm(prev => {
            const types = prev.payment_method_types.includes(method)
                ? prev.payment_method_types.filter(m => m !== method)
                : [...prev.payment_method_types, method];
            return { ...prev, payment_method_types: types.length > 0 ? types : prev.payment_method_types };
        });
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PaymentIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Stripe Payments</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage payment intents and refunds</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchPayments}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Refresh
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Create Payment
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Payments Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Payment ID', 'Amount', 'Currency', 'Status', 'Created', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => <TableRow key={i}><TableCell colSpan={6}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : payments.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                        <PaymentIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No payment intents found</Typography>
                                    </TableCell></TableRow>
                                ) : payments.map(p => {
                                    const statusStyle = STATUS_COLORS[p.status] || { bg: '#f1f5f9', color: '#64748b' };
                                    return (
                                        <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#1e293b', fontWeight: 500 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontFamily: 'monospace' }}>{p.id}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                                                {formatCurrency(p.amount, p.currency)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase' }}>
                                                {p.currency}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={p.status?.replace(/_/g, ' ')} size="small"
                                                    sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(p.created)}
                                            </TableCell>
                                            <TableCell>
                                                {p.status === 'succeeded' && (
                                                    <Tooltip title="Refund">
                                                        <IconButton size="small" onClick={() => { setRefundDialog({ open: true, payment: p }); setRefundAmount(''); }}
                                                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                        >
                                                            <UndoIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Create Payment Intent Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Create Payment Intent</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Amount" value={createForm.amount}
                        onChange={e => setCreateForm(prev => ({ ...prev, amount: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                            inputProps: { min: 0.5, step: '0.01' },
                        }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontFamily }}>Currency</InputLabel>
                        <Select value={createForm.currency} onChange={e => setCreateForm(prev => ({ ...prev, currency: e.target.value }))}
                            label="Currency" sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {CURRENCY_OPTIONS.map(c => <MenuItem key={c} value={c} sx={{ fontFamily }}>{c.toUpperCase()}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#475569', mb: 1 }}>Payment Methods</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {PAYMENT_METHOD_OPTIONS.map(pm => (
                                <Chip key={pm.value} label={pm.label} size="small"
                                    onClick={() => togglePaymentMethod(pm.value)}
                                    sx={{
                                        fontFamily, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                                        bgcolor: createForm.payment_method_types.includes(pm.value) ? '#dcfce7' : '#f1f5f9',
                                        color: createForm.payment_method_types.includes(pm.value) ? '#166534' : '#64748b',
                                        border: createForm.payment_method_types.includes(pm.value) ? '1px solid #22c55e' : '1px solid #e2e8f0',
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate}
                        disabled={creating || !createForm.amount || parseFloat(createForm.amount) < 0.5}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Refund Dialog */}
            <Dialog open={refundDialog.open} onClose={() => setRefundDialog({ open: false, payment: null })} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Refund Payment</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    {refundDialog.payment && (
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                            Original amount: {formatCurrency(refundDialog.payment.amount, refundDialog.payment.currency)}
                        </Typography>
                    )}
                    <TextField label="Refund Amount (leave blank for full refund)" value={refundAmount}
                        onChange={e => setRefundAmount(e.target.value)}
                        fullWidth size="small" type="number"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                            inputProps: { min: 0.01, step: '0.01' },
                        }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setRefundDialog({ open: false, payment: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleRefund} disabled={refunding}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {refunding ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Refund'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
